/**
 * Swipe-to-dismiss for an ephemeral surface (a toast). Slide it horizontally to
 * throw it off-screen; release past the commit threshold (or fling) to dismiss,
 * otherwise it snaps back to rest.
 *
 * Reuses the shared recognizer so it gets the exact premium feel of the row
 * swipe: 8px deadzone, axis-lock (so a vertical drag yields to native scroll and
 * never fights the page), fling detection, a rising-edge haptic at the commit
 * threshold, and `prefers-reduced-motion` (instant dismiss, no slide). Either
 * direction dismisses — a toast has no "edit" affordance, so left and right both
 * throw it away. Opacity fades with travel for a tactile "peeling off" feel.
 *
 * Bind it to a per-toast surface with `touch-action: pan-y` so vertical scroll
 * stays native while the horizontal axis is ours. Drags that begin on the close
 * button are ignored so the button keeps working.
 */

import { type Accessor, createSignal, onCleanup } from "solid-js";

import { closestSelector } from "./guards";
import { prefersReducedMotion } from "./motion";
import { applyRubberBand, createGestureRecognizer } from "./recognizer";
import { type PointerHandlers, pointerHandlers } from "./solid";
import { TOAST_SWIPE } from "./presets";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fadeDistancePx: FADE_DISTANCE_PX,
  fling: FLING,
} = TOAST_SWIPE;

interface SwipeToDismissOptions {
  getSurfaceRef: () => HTMLElement | undefined;
  /** Fires once the toast has been flung/swiped away. */
  onDismiss: () => void;
}

export interface SwipeToDismissApi {
  /** JSX pointer handlers to bind on the surface. */
  handlers: PointerHandlers;
  /** Horizontal offset for the surface (px). */
  tx: Accessor<number>;
  /** 0..1 opacity that fades as the toast is dragged away. */
  opacity: Accessor<number>;
  isSwiping: Accessor<boolean>;
  isAnimating: Accessor<boolean>;
}

export function useSwipeToDismiss(options: SwipeToDismissOptions): SwipeToDismissApi {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  const surfaceWidth = () => options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;

  const opacity = () => {
    const traveled = Math.min(Math.abs(tx()), FADE_DISTANCE_PX);
    return 1 - (traveled / FADE_DISTANCE_PX) * 0.7;
  };

  const settleBack = () => {
    setTx(0);
    setTimeout(() => setIsAnimating(false), CANCEL_ANIMATION_MS);
  };

  const flyOut = (toRight: boolean) => {
    if (prefersReducedMotion()) {
      options.onDismiss();
      return;
    }
    setTx(toRight ? surfaceWidth() : -surfaceWidth());
    setTimeout(() => {
      options.onDismiss();
    }, COMMIT_ANIMATION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    fling: FLING,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) =>
      // Let the close button receive its tap; ignore drags that start on it.
      !closestSelector(target, 'button, a, input, [data-no-swipe="true"]'),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const committed = Math.abs(finalTx) > COMMIT_THRESHOLD_PX || sample.isFling;

      if (committed) {
        flyOut(finalTx > 0);
      } else {
        settleBack();
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      settleBack();
    },
  });

  const handlers = pointerHandlers(recognizer);
  onCleanup(() => {
    recognizer.cancel();
  });

  return { handlers, tx, opacity, isSwiping, isAnimating };
}
